import "./aiUsage"; // AI usage meter (logs every paid model call)
/**
 * OpenRouter image generation client for Studio (GPT Image 2.5).
 * Dedicated Image API: POST https://openrouter.ai/api/v1/images
 * Docs: https://openrouter.ai/docs/guides/overview/multimodal/image-generation
 *
 * Two lanes:
 * - normal → Sunburst (precision, real image work)
 * - spark  → Flare (speed, drafts such as workflow banners)
 */
export const OPENROUTER_IMAGES_API_URL =
  "https://openrouter.ai/api/v1/images";

export const OPENROUTER_IMAGE_MODEL_IDS = {
  /** Precision lane — OpenRouter slug `openai/gpt-image-2.5-sunburst`. */
  image: "openai/gpt-image-2.5-sunburst",
  /** Speed lane — OpenRouter slug `openai/gpt-image-2.5-flare`. Studio calls this Spark. */
  spark: "openai/gpt-image-2.5-flare",
} as const;

export type ImageModelLane = "spark" | "normal";

/** GPT Image 2 aspect ratios accepted by the OpenRouter endpoint. */
const GPT_IMAGE_2_ASPECTS = new Set([
  "1:1",
  "3:2",
  "2:3",
  "4:3",
  "3:4",
  "16:9",
  "9:16",
  "21:9",
  "auto",
]);

export function openRouterApiKey(): string {
  const key = process.env.OPENROUTER_API_KEY?.trim();
  if (!key) {
    throw new Error("OPENROUTER_API_KEY is not configured");
  }
  return key;
}

export function resolveOpenRouterImageModelId(
  modelId: string | undefined | null,
  lane?: ImageModelLane,
): string {
  const raw = modelId?.trim() || "";
  const lower = raw.toLowerCase();
  const sparkAlias =
    !raw ||
    lower === "spark" ||
    lower === "flare" ||
    lower.includes("gpt-image-2.5-flare");
  if (lane === "spark" && sparkAlias) {
    return OPENROUTER_IMAGE_MODEL_IDS.spark;
  }
  if (
    lower === "spark" ||
    lower === "flare" ||
    lower.includes("gpt-image-2.5-flare")
  ) {
    return OPENROUTER_IMAGE_MODEL_IDS.spark;
  }
  if (
    !raw ||
    lower === "normal" ||
    lower === "sunburst" ||
    lower.includes("gpt-image-2.5-sunburst") ||
    lower === "openai/gpt-image-2.5" ||
    lower === "gpt-image-2.5" ||
    lower === "openai/gpt-image-2" ||
    lower === "gpt-image-2" ||
    lower.includes("seedream") ||
    lower.includes("dola-seedream")
  ) {
    return OPENROUTER_IMAGE_MODEL_IDS.image;
  }
  if (lower.includes("gpt-image-1-mini")) return "openai/gpt-image-1-mini";
  if (lower.includes("gpt-image-1") && !lower.includes("gpt-image-2")) {
    return "openai/gpt-image-1";
  }
  return raw;
}

/**
 * GPT Image 2 ignores 1K/2K/4K pixel tiers — Studio keeps them as
 * cost/UX chips only. Map Studio aspect to a supported aspect_ratio.
 */
export function gptImageAspectRatio(
  aspectRatio: string | undefined,
): string | undefined {
  if (!aspectRatio?.trim()) return undefined;
  const raw = aspectRatio.trim();
  if (GPT_IMAGE_2_ASPECTS.has(raw)) return raw;
  const match = raw.match(/^(\d+)\s*:\s*(\d+)$/);
  if (!match) return "auto";
  const key = `${match[1]}:${match[2]}`;
  if (GPT_IMAGE_2_ASPECTS.has(key)) return key;
  if (key === "1:4" || key === "4:1") return key === "1:4" ? "2:3" : "3:2";
  if (key === "4:5" || key === "5:4") return key === "4:5" ? "3:4" : "4:3";
  if (key === "1:1") return "1:1";
  const w = Number(match[1]);
  const h = Number(match[2]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return "auto";
  }
  return w >= h ? "16:9" : "9:16";
}

export type OpenRouterImageResult = {
  data: Uint8Array;
  mediaType: string;
};

/**
 * OpenAI Images `moderation` knob (auto | low). Studio sends `low` by default:
 * `auto` blocks ordinary fashion/beauty ads (evening gowns, velvet dresses).
 * OpenRouter forwards it as a provider passthrough:
 * `provider.options.openai.moderation` (see the model's
 * `allowed_passthrough_parameters` on /api/v1/images/models/<id>/endpoints).
 * Override globally with STUDIO_IMAGE_MODERATION=auto.
 */
export type GptImageModeration = "auto" | "low";

export function resolveGptImageModeration(
  value: string | undefined | null,
): GptImageModeration {
  const raw = (value ?? process.env.STUDIO_IMAGE_MODERATION ?? "").trim().toLowerCase();
  return raw === "auto" ? "auto" : "low";
}

/** OpenAI-served models (gpt-image-*) accept the `moderation` passthrough. */
export function supportsModerationPassthrough(model: string): boolean {
  return model.trim().toLowerCase().startsWith("openai/");
}

export type ImageModerationStage = "input" | "output" | "unknown";

export type ImageProviderErrorDetails = {
  status: number;
  code?: string;
  type?: string;
  message: string;
  provider?: string;
  requestId?: string;
  /** Where the safety system stopped it: the prompt/refs (input) or the rendered image (output). */
  moderationStage?: ImageModerationStage;
  /** safety_violations as the provider returned them (OpenRouter may cut the text short). */
  categories?: string[];
  /** True when the provider text ends in "…" (OpenRouter shortens upstream messages). */
  providerTruncated?: boolean;
};

/** Structured provider failure. `message` is the full text — never sliced. */
export class OpenRouterImageError extends Error {
  readonly details: ImageProviderErrorDetails;
  constructor(details: ImageProviderErrorDetails) {
    super(formatImageProviderError(details));
    this.name = "OpenRouterImageError";
    this.details = details;
  }
}

export function isModerationBlockText(text: string): boolean {
  return /moderation_blocked|safety system|safety_violations/i.test(text);
}

/** Pull request id + safety_violations out of an OpenAI moderation message. */
export function parseModerationMessage(message: string): {
  requestId?: string;
  categories?: string[];
  truncated: boolean;
} {
  const requestId = message.match(/\b(req_[a-z0-9]+)/i)?.[1];
  const truncated = /…\s*$|\.\.\.\s*$/.test(message.trim());
  const violations = message.match(/safety_violations\s*=\s*\[([^\]]*)\]?/i)?.[1];
  const categories = violations
    ? violations
        .split(",")
        .map((part) => part.replace(/["'\s]/g, "").trim())
        .filter(Boolean)
    : undefined;
  return { requestId, categories, truncated };
}

/**
 * One line with everything an operator needs: status, code, stage,
 * categories, request id, and the provider's full message.
 */
export function formatImageProviderError(details: ImageProviderErrorDetails): string {
  const bits: string[] = [];
  if (details.code) bits.push(details.code);
  if (details.moderationStage) bits.push(`stage=${details.moderationStage}`);
  if (details.categories?.length) {
    bits.push(`categories=[${details.categories.join(", ")}]`);
  }
  if (details.requestId) bits.push(`request_id=${details.requestId}`);
  if (details.provider) bits.push(`provider=${details.provider}`);
  const head = `OpenRouter image ${details.status}${bits.length ? ` (${bits.join("; ")})` : ""}`;
  const tail = details.providerTruncated
    ? " [OpenRouter shortened the provider text at the …]"
    : "";
  return `${head}: ${details.message}${tail}`;
}

function buildProviderErrorDetails(args: {
  status: number;
  error?: { message?: string; code?: string | number; type?: string; metadata?: { provider_name?: string } } | null;
  fallbackMessage?: string;
  partialImages: number;
  streamed: boolean;
}): ImageProviderErrorDetails {
  const message = String(
    args.error?.message || args.fallbackMessage || `OpenRouter HTTP ${args.status}`,
  );
  const code =
    args.error?.code != null && typeof args.error.code === "string"
      ? args.error.code
      : undefined;
  const details: ImageProviderErrorDetails = {
    status: args.status,
    code,
    type: args.error?.type,
    message,
    provider: args.error?.metadata?.provider_name,
  };
  if (code === "moderation_blocked" || isModerationBlockText(message)) {
    const parsed = parseModerationMessage(message);
    details.code = details.code ?? "moderation_blocked";
    details.requestId = parsed.requestId;
    details.categories = parsed.categories;
    details.providerTruncated = parsed.truncated || undefined;
    // Streaming tells us which gate fired: partial renders before the error
    // mean the prompt passed and the finished image was blocked.
    details.moderationStage = args.streamed
      ? args.partialImages > 0
        ? "output"
        : "input"
      : "unknown";
  }
  return details;
}

type OpenRouterStreamEvent = {
  type?: string;
  b64_json?: string;
  media_type?: string;
  error?: { message?: string; code?: string | number; type?: string };
};

/** Parse an Images API SSE body (partial_image / completed / error events). */
export function parseOpenRouterImageStream(text: string): {
  images: Array<{ b64_json: string; media_type?: string }>;
  partialImages: number;
  error?: OpenRouterStreamEvent["error"];
} {
  const images: Array<{ b64_json: string; media_type?: string }> = [];
  let partialImages = 0;
  let error: OpenRouterStreamEvent["error"] | undefined;
  for (const line of text.split(/\r?\n/)) {
    if (!line.startsWith("data:")) continue;
    const payload = line.slice(5).trim();
    if (!payload || payload === "[DONE]") continue;
    let event: OpenRouterStreamEvent;
    try {
      event = JSON.parse(payload) as OpenRouterStreamEvent;
    } catch {
      continue;
    }
    if (event.type === "image_generation.partial_image") {
      partialImages += 1;
    } else if (event.type === "image_generation.completed" && event.b64_json) {
      images.push({ b64_json: event.b64_json, media_type: event.media_type });
    } else if (event.type === "error" || event.error) {
      error = event.error ?? { message: "Image stream failed" };
    }
  }
  return { images, partialImages, error };
}

type OpenRouterImageResponse = {
  data?: Array<{
    b64_json?: string;
    url?: string;
    media_type?: string;
  }>;
  error?: {
    message?: string;
    code?: string | number;
    type?: string;
    metadata?: { provider_name?: string };
  };
  message?: string;
};

/**
 * Generate one image via OpenRouter GPT Image 2.
 * quality: Studio low | medium | high → GPT quality knob.
 * Reference URLs must be http(s) or data: — we normalize to data URLs so
 * OpenRouter never sees Seedance `asset://` or opaque CDN schemes.
 */
export async function generateOpenRouterImage(args: {
  modelId?: string;
  prompt: string;
  aspectRatio?: string;
  quality?: string;
  referenceUrls?: string[];
  /** OpenAI moderation knob; default "low" (see resolveGptImageModeration). */
  moderation?: GptImageModeration;
}): Promise<OpenRouterImageResult[]> {
  const prompt = args.prompt?.trim();
  if (!prompt) throw new Error("Prompt is required");
  const model = resolveOpenRouterImageModelId(
    args.modelId || process.env.GATEWAY_IMAGE_MODEL_ID || undefined,
  );
  const quality = normalizeGptImageQuality(args.quality);
  const aspect_ratio = gptImageAspectRatio(args.aspectRatio);
  const rawRefs = (args.referenceUrls ?? []).map((u) => u.trim()).filter(Boolean);
  const refs: string[] = [];
  for (const url of rawRefs.slice(0, 16)) {
    refs.push(await toOpenRouterImageRef(url));
  }

  const body: Record<string, unknown> = {
    model,
    prompt,
    n: 1,
    quality,
    output_format: "png",
  };
  if (aspect_ratio) body.aspect_ratio = aspect_ratio;
  const openAiModel = supportsModerationPassthrough(model);
  if (openAiModel) {
    body.provider = {
      options: {
        openai: { moderation: resolveGptImageModeration(args.moderation) },
      },
    };
    // Stream text-to-image only, so a moderation error tells us input vs
    // output stage (partial renders arrive before an output-stage block).
    // OpenRouter rejects streaming for image-to-image (edit) requests, so any
    // call with reference images goes non-streaming (moderation still low).
    if (!refs.length) body.stream = true;
  }
  if (refs.length) {
    body.input_references = refs.map((url) => ({
      type: "image_url",
      image_url: { url },
    }));
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${openRouterApiKey()}`,
    "Content-Type": "application/json",
  };
  const referer =
    process.env.SITE_URL?.trim() || process.env.CONVEX_SITE_URL?.trim();
  if (referer) {
    headers["HTTP-Referer"] = referer;
    try {
      headers["X-Title"] = `Yatishara Studio (${new URL(referer).hostname})`;
    } catch {
      headers["X-Title"] = "Yatishara Studio";
    }
  }

  const postImages = () =>
    fetch(OPENROUTER_IMAGES_API_URL, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  let response = await postImages();
  if (!response.ok && body.stream) {
    // Belt and braces: if OpenRouter refuses streaming for this request,
    // retry once without it instead of failing the job.
    const probe = await response.clone().text().catch(() => "");
    if (/streaming is not supported/i.test(probe)) {
      delete body.stream;
      response = await postImages();
    }
  }
  const contentType = response.headers.get("content-type") ?? "";
  let items: NonNullable<OpenRouterImageResponse["data"]> = [];
  if (response.ok && contentType.includes("text/event-stream")) {
    const parsed = parseOpenRouterImageStream(await response.text());
    if (parsed.error) {
      throw new OpenRouterImageError(
        buildProviderErrorDetails({
          // Stream errors arrive on a 200; report the provider's own status.
          status: parsed.error.code === "moderation_blocked" ? 400 : 502,
          error: {
            ...parsed.error,
            metadata: {
              provider_name: response.headers.get("x-provider-name") ?? undefined,
            },
          },
          partialImages: parsed.partialImages,
          streamed: true,
        }),
      );
    }
    items = parsed.images;
  } else {
    const rawText = await response.text().catch(() => "");
    let json: OpenRouterImageResponse | null = null;
    try {
      json = rawText ? (JSON.parse(rawText) as OpenRouterImageResponse) : null;
    } catch {
      json = null;
    }
    if (!response.ok || json?.error) {
      throw new OpenRouterImageError(
        buildProviderErrorDetails({
          status: response.ok ? 502 : response.status,
          error: json?.error as never,
          fallbackMessage: json?.message || rawText.slice(0, 2000) || undefined,
          partialImages: 0,
          streamed: false,
        }),
      );
    }
    items = json?.data ?? [];
  }
  if (!items.length) {
    throw new Error("GPT Image 2.5 returned no images");
  }
  const out: OpenRouterImageResult[] = [];
  for (const item of items) {
    if (item.b64_json) {
      out.push({
        data: new Uint8Array(Buffer.from(item.b64_json, "base64")),
        mediaType: item.media_type?.split(";")[0]?.trim() || "image/png",
      });
      continue;
    }
    if (item.url) {
      const media = await downloadMedia(item.url);
      out.push(media);
    }
  }
  if (!out.length) {
    throw new Error("GPT Image 2.5 returned empty image payloads");
  }
  return out;
}

/** OpenAI/OpenRouter image refs: data:image… or http(s). Convert everything else. */
async function toOpenRouterImageRef(url: string): Promise<string> {
  const trimmed = url.trim();
  if (/^data:image\//i.test(trimmed)) return trimmed;
  if (/^asset:\/\//i.test(trimmed)) {
    throw new Error(
      "Image references cannot use Seedance asset:// URLs. Re-attach the Studio image (HTTPS) and retry.",
    );
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    const scheme = trimmed.split(":", 1)[0] || "unknown";
    throw new Error(
      `Unsupported image URL scheme (${scheme}). OpenRouter needs base64 data URLs or HTTP/HTTPS.`,
    );
  }
  const response = await fetch(trimmed);
  if (!response.ok) {
    throw new Error(`Failed to fetch reference image (${response.status})`);
  }
  const buffer = Buffer.from(await response.arrayBuffer());
  if (!buffer.length) {
    throw new Error("Reference image download was empty");
  }
  const mime =
    response.headers.get("content-type")?.split(";")[0]?.trim() ||
    guessImageMime(trimmed) ||
    "image/png";
  if (!mime.toLowerCase().startsWith("image/")) {
    throw new Error(`Reference URL did not return an image (${mime})`);
  }
  // Cap ~12MB decoded — OpenRouter payload limits; keep refs usable.
  if (buffer.length > 12 * 1024 * 1024) {
    throw new Error("Reference image is too large (max 12MB). Use a smaller original.");
  }
  return `data:${mime};base64,${buffer.toString("base64")}`;
}

function guessImageMime(url: string): string | undefined {
  const path = url.split("?")[0]?.toLowerCase() ?? "";
  if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
  if (path.endsWith(".webp")) return "image/webp";
  if (path.endsWith(".gif")) return "image/gif";
  if (path.endsWith(".png")) return "image/png";
  return undefined;
}

async function downloadMedia(url: string): Promise<OpenRouterImageResult> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download OpenRouter media (${response.status})`);
  }
  const buffer = new Uint8Array(await response.arrayBuffer());
  const mediaType =
    response.headers.get("content-type")?.split(";")[0]?.trim() || "image/png";
  return { data: buffer, mediaType };
}

function normalizeGptImageQuality(quality: string | undefined): string {
  const lower = (quality ?? "medium").trim().toLowerCase();
  if (lower === "low" || lower === "medium" || lower === "high") return lower;
  return "medium";
}

export function formatOpenRouterError(error: unknown): string {
  if (error instanceof Error) {
    // Full text: moderation stage + categories must survive to the UI/MCP.
    return error.message;
  }
  return "OpenRouter image request failed";
}

/** True when a resolved model id routes through OpenRouter (not Ark). */
export function isOpenRouterImageModel(modelId: string): boolean {
  const lower = modelId.trim().toLowerCase();
  return (
    lower.includes("gpt-image") ||
    lower === "openai/gpt-image-2" ||
    lower.startsWith("openai/")
  );
}
